import { CoverType, CoverReason } from '@general/js/enums';
import { concatenateStrings } from './StringTransformUtils';

/**
 * Map the cover composition data to required display value to display on the page
 *
 * @param {array} coverCompositionData - The array that contains all the value of cover composition options
 * @returns {string} - The mapped value to display on the page
 */
export const summaryCoverComposition = (coverCompositionData) => {
  if (coverCompositionData !== null) {
    switch (coverCompositionData) {
      case 'single':
        return 'single person';
      case 'couple':
        return 'couple';
      case 'childrenOnly':
        return 'child/children only';
      case 'singleParent':
        return 'single parent family';
      case 'singleParentPlus':
        return 'single parent family+';
      case 'familyPlus':
        return 'family+';
      default:
        return 'family';
    }
  }
  return null;
};

/**
 * Map the cover type data to required display value to display on the page
 *
 * @param {array} coverTypeData - The array that contains all the value of cover type options
 * @returns {string} - The mapped value to display on the page
 */
export const summaryCoverType = (coverTypeData) => {
  if (coverTypeData) {
    // only enter switch if coverTypeData is non-falsy, i.e not null, undefined or ''
    switch (coverTypeData.toLowerCase()) {
      case 'hospital':
      case 'hospital only':
        return CoverType.HOSPITAL_ONLY;
      case 'extras':
      case 'extras only':
        return CoverType.EXTRAS_ONLY;
      default:
        return CoverType.PACKAGE;
    }
  }
  return null;
};

/**
 * Map the cover reason data to required display value to display on the page
 *
 * @param {array} coverReasonData - The array that contains all the value of cover reason options
 * @returns {string} - The mapped value to display on the page
 */
export const summaryCoverReason = (coverReasonData) => {
  if (coverReasonData !== null) {
    switch (coverReasonData.toLowerCase()) {
      case 'specific':
      case 'specific treatment':
        return CoverReason.SPECIFIC_TREATMENT;
      case 'planningfamily':
      case 'planning a family':
        return CoverReason.PLANNING_A_FAMILY;
      case 'peaceofmind':
      case 'total peace of mind':
        return CoverReason.PEACE_OF_MIND;
      case 'basics':
      case 'just the basics':
        return CoverReason.BASICS;
      default:
        return CoverReason.GOOD_ALL_ROUND_COVER;
    }
  }
  return null;
};

/**
 * Map the rebate tier data to required display value to display on the page
 *
 * @param {array} allTiers - The array that contains all the object of display and returnValue for range and description
 * @param {string} selectedTier - The selected value of rebate tier from the user
 * @returns {string} - The mapped value to display on the page
 */
export const summaryRebate = (allTiers, selectedTier) => {
  if (selectedTier !== null && allTiers !== null && selectedTier !== '') {
    return allTiers
      .find((x) => {
        return x.returnValue === selectedTier;
      })
      .display.toLowerCase();
  }
  return null;
};

/**
 * Map the eligibility data to required display value to display on the page
 *
 * @param {array} eligibility - The array that contains all the value of eligibility options
 * @returns {string} - The mapped value to display on the page
 */
export const summaryEligibility = (eligibility) => {
  if (eligibility != null) {
    switch (eligibility) {
      case 'ADF':
        return 'I/My partner is currently serving';
      case 'ExServing':
        return 'I/My partner has served';
      case 'Supporting':
        return 'I/My partner is related to someone who is eligible to join';
      default:
        return 'I/My partner work for a Defence company or Dept of Defence';
    }
  }
  return null;
};

/**
 * Generates a comma delimited string of treatments using the provided input parameters.
 *
 * @param {array} hospitalNeeds - Array of hospital treatment need strings.
 * @param {array} extrasNeeds - Array of extra treatment need strings.
 * @returns {string} - A single comma delimited string representing the contents of both arrays.
 */
export const generateSpecificTreatmentsList = (hospitalNeeds, extrasNeeds) => {
  if (hospitalNeeds == null && extrasNeeds == null) return undefined;
  if (Number.isNaN(hospitalNeeds) && Number.isNaN(extrasNeeds)) return undefined;

  let hospitalCategories = hospitalNeeds;
  let extrasCategories = extrasNeeds;

  if (hospitalNeeds == null || hospitalNeeds === '') {
    hospitalCategories = [];
  } else if (!Array.isArray(hospitalNeeds) && !Number.isNaN(hospitalNeeds)) {
    // Not an array but is a number
    hospitalCategories = [hospitalNeeds];
  }

  if (extrasNeeds == null || extrasNeeds === '') {
    extrasCategories = [];
  } else if (!Array.isArray(extrasNeeds) && !Number.isNaN(extrasNeeds)) {
    // Not an array but is a number
    extrasCategories = [extrasNeeds];
  }

  const combinedArrays = hospitalCategories.concat(extrasCategories);
  return concatenateStrings(combinedArrays);
};

/**
 * Returns only the unique product guides based on if guide url is the same
 */
export const getUniqueProductGuides = (products, combinedName) => {
  const uniqueProductGuideUrls = [];
  products.forEach((product) => {
    const foundDuplicateProduct = uniqueProductGuideUrls.find((existingProduct) => {
      return existingProduct.guideUrl === product.guideUrl;
    });
    if (foundDuplicateProduct) {
      foundDuplicateProduct.name = combinedName;
    } else {
      uniqueProductGuideUrls.push({ name: product.name, guideUrl: product.guideUrl });
    }
  });
  return uniqueProductGuideUrls;
};
